import { modeDemonstrationActif } from './demo.js';
import { fichierFecDemo } from './demoComptabilite.js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import { api, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
export interface CompteComptable {
  numero: string;
  libelle: string;
}
export interface PlanComptable {
  journalVentes: { code: string; libelle: string };
  journalTresorerie: { code: string; libelle: string };
  clients: CompteComptable;
  ventes: Record<'normal' | 'reduit_5_5' | 'marge' | 'exonere', CompteComptable>;
  tva: Record<'normal' | 'reduit_5_5' | 'marge', CompteComptable>;
  droitDeSuite: CompteComptable;
  tresorerie: Record<'virement' | 'carte' | 'cheque' | 'especes' | 'autre', CompteComptable>;
}
export interface ReponsePlan {
  plan: PlanComptable;
  personnalise: boolean;
}
export interface ApercuFec {
  du: string;
  au: string;
  nomFichier: string | null;
  pieces: number;
  reglements: number;
  lignes: number;
  exclues: { numero: string; motif: 'devise' | 'desequilibre' | 'sans_totaux' }[];
  avertissements: string[];
}
const chemin = (g: string) => `/g/${encodeURIComponent(g)}/comptabilite`;
const periode = (du: string, au: string) =>
  `du=${encodeURIComponent(du)}&au=${encodeURIComponent(au)}`;
export const comptabiliteApi = {
  lirePlan: (g: string, appel?: AppelApi) =>
    api.get<ReponsePlan>(`${chemin(g)}/plan`, { galerie: g, ...appel }),
  ecrirePlan: (g: string, plan: PlanComptable, appel?: AppelApi) =>
    api.put<ReponsePlan>(`${chemin(g)}/plan`, plan, { galerie: g, ...appel }),
  apercuFec: (g: string, du: string, au: string, appel?: AppelApi) =>
    api.get<ApercuFec>(`${chemin(g)}/fec?${periode(du, au)}&format=json`, { galerie: g, ...appel }),
  telechargerFec: (g: string, du: string, au: string, appel?: AppelApi) =>
    modeDemonstrationActif()
      ? Promise.resolve(fichierFecDemo(du, au))
      : requeteTelechargement(`${chemin(g)}/fec?${periode(du, au)}`, { galerie: g, ...appel }),
};
export function usePlanComptable(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'comptabilite', 'plan'),
    queryFn: (c) => comptabiliteApi.lirePlan(g, appelDeRequete(c)),
  });
}
export function useEcrirePlanComptable(g: string) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: (p: PlanComptable, appel) => comptabiliteApi.ecrirePlan(g, p, appel),
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: cleMetier(g, 'comptabilite') });
      },
    }),
  );
}
export function useApercuFec(g: string, du: string, au: string, actif: boolean) {
  return useQuery({
    queryKey: cleMetier(g, 'comptabilite', 'fec', du, au),
    queryFn: (c) => comptabiliteApi.apercuFec(g, du, au, appelDeRequete(c)),
    enabled: actif,
    staleTime: 0,
  });
}
